import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useConfig } from "../lib/ConfigContext";
import {
  fetchListingSchema,
  normalizeListingSchema,
  DEFAULT_LISTING_SCHEMA,
  FIELD_SECTIONS,
  FIELD_TYPES,
  FILTER_UI_TYPES,
  FILTER_PLACEMENTS,
  ROLES,
  TRANSACTION_TYPES,
  DEFAULT_FLOOR_OPTIONS,
  DISPLAY_SURFACES,
  DISPLAY_SURFACE_LABELS,
  RENDERER_HINTS,
  DEFAULT_DISPLAY_VISIBILITY,
  DISPLAY_ZONES,
  DISPLAY_ZONE_LABELS,
} from "../lib/listingSchema";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Save, Plus, Trash2, Lock, Sliders, Eye, EyeOff, FormInput, Filter as FilterIcon, AlertTriangle, ChevronDown, ChevronRight, Activity, CheckCircle2, XCircle, Building2, Layers } from "lucide-react";
import AdminProjectSchemaTab from "./AdminProjectSchemaTab";
import AdminPropertyTypesTab from "./AdminPropertyTypesTab";

/**
 * AdminFiltersTab — v2-listing-schema (rebuilt 2026-06-21)
 * --------------------------------------------------------
 * Splits filter management into TWO clearly separated surfaces:
 *
 *   Tab 1 · Form Fields   — manages what appears on the listing
 *                            submission form. Each field carries its
 *                            own type, options, required flag, role
 *                            visibility, property-type / transaction
 *                            visibility, section, sort order.
 *
 *   Tab 2 · Search Filters — manages public listing-page filters. Each
 *                            filter LINKS to a form field
 *                            (linkedFieldKey) so options inherit from
 *                            the field automatically — no duplicate
 *                            option lists.
 *
 * Save model: bulk PUT /api/admin/listing-schema. The backend validates
 * the entire envelope before any write so partial state is impossible.
 */

const SCHEMA_TABS = [
  { key: "fields", label: "Listing · Form Fields", Icon: FormInput },
  { key: "filters", label: "Listing · Search Filters", Icon: FilterIcon },
  { key: "property_types", label: "Property Types", Icon: Layers },
  { key: "project", label: "Project Schema", Icon: Building2 },
];

const DEFAULT_FIELD = {
  key: "",
  label: "",
  type: "dropdown",
  section: "Property Details",
  required: false,
  active: true,
  locked: false,
  options: [],
  placeholder: "",
  helpText: "",
  defaultValue: null,
  sortOrder: 100,
  showOnForm: true,
  roleVisibility: { admin: true, team: true, dealer: true, builder: true, client: true, public: true },
  propertyTypes: [],
  transactionTypes: [],
  validation: {},
  displayVisibility: { ...DEFAULT_DISPLAY_VISIBILITY },
  displayRenderer: "text",
  displayZones: { form: true, moreDetails: true },
};

const DEFAULT_FILTER = {
  key: "",
  linkedFieldKey: "",
  label: "",
  uiType: "multi-select",
  placement: "more",
  active: true,
  sortOrder: 100,
  visibility: { admin: true, team: true, dealer: true, builder: true, client: true, public: true },
  mobileVisible: true,
  desktopVisible: true,
  defaultCollapsed: false,
};

export default function AdminFiltersTab() {
  const [activeTab, setActiveTab] = useState("fields");
  const [schema, setSchema] = useState(DEFAULT_LISTING_SCHEMA);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const [showAddField, setShowAddField] = useState(false);
  const [showAddFilter, setShowAddFilter] = useState(false);
  const [healthReport, setHealthReport] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const { refreshListingSchema } = useConfig();

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const r = await api.get("/admin/listing-schema/health-check");
      setHealthReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health check failed");
    }
    setHealthLoading(false);
  };

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const { schema: s, error } = await fetchListingSchema(api, { tryAdmin: true });
    setSchema(normalizeListingSchema(s));
    setDirty(false);
    if (!silent) setLoading(false);
    // Silent fallback — never spam the user with toast errors.
    // Error stays in console only.
    if (error) {
      console.warn("[admin-filters] loaded fallback schema:", error);
    }
  };

  useEffect(() => { load(); }, []);

  const fieldByKey = useMemo(() => Object.fromEntries(schema.fields.map((f) => [f.key, f])), [schema.fields]);

  // Warnings — surfaced inline
  const warnings = useMemo(() => {
    const w = [];
    // Filter linked to missing or inactive field
    for (const fl of schema.filters) {
      const fld = fieldByKey[fl.linkedFieldKey];
      if (!fld) w.push({ severity: "error", kind: "missing-field", msg: `Filter "${fl.label}" links to missing field "${fl.linkedFieldKey}".` });
      else if (!fld.active) w.push({ severity: "warn", kind: "inactive-field", msg: `Filter "${fl.label}" links to inactive field "${fl.linkedFieldKey}".` });
    }
    // Required field hidden from form
    for (const f of schema.fields) {
      if (f.required && !f.showOnForm) w.push({ severity: "warn", kind: "required-hidden", msg: `Field "${f.label}" is REQUIRED but hidden from the form.` });
    }
    return w;
  }, [schema, fieldByKey]);

  const updateField = (key, patch) => {
    setSchema((s) => ({ ...s, fields: s.fields.map((f) => f.key === key ? { ...f, ...patch } : f) }));
    setDirty(true);
  };

  const updateFilter = (key, patch) => {
    setSchema((s) => ({ ...s, filters: s.filters.map((f) => f.key === key ? { ...f, ...patch } : f) }));
    setDirty(true);
  };

  const addField = (field) => {
    setSchema((s) => ({ ...s, fields: [...s.fields, { ...DEFAULT_FIELD, ...field }] }));
    setDirty(true);
  };
  const removeField = (key) => {
    const f = fieldByKey[key];
    if (f?.locked) { toast.error("Locked field cannot be deleted (use Active toggle)"); return; }
    if (!window.confirm(`Delete field "${f?.label || key}"?`)) return;
    setSchema((s) => ({
      ...s,
      fields: s.fields.filter((x) => x.key !== key),
      // Drop dependent filters too
      filters: s.filters.filter((x) => x.linkedFieldKey !== key),
    }));
    setDirty(true);
  };

  const addFilter = (filter) => {
    const linked = fieldByKey[filter.linkedFieldKey];
    if (!linked) { toast.error("Choose a linked field first"); return; }
    setSchema((s) => ({
      ...s,
      filters: [
        ...s.filters,
        {
          ...DEFAULT_FILTER,
          ...filter,
          label: filter.label || linked.label,
        },
      ],
    }));
    setDirty(true);
  };

  const removeFilter = (key) => {
    if (!window.confirm("Delete this filter? The linked field stays.")) return;
    setSchema((s) => ({ ...s, filters: s.filters.filter((x) => x.key !== key) }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/listing-schema", schema);
      const next = normalizeListingSchema(r.data);
      setSchema(next);
      setDirty(false);
      toast.success("Schema saved");
      refreshListingSchema?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Save failed";
      toast.error(detail);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-zinc-500" data-testid="admin-filters-loading">Loading schema…</div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-filters-tab">
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex-1 min-w-[320px]">
            <h3 className="font-heading font-semibold text-lg tracking-tight inline-flex items-center gap-2">
              <Sliders size={16} /> Listing Schema Builder
            </h3>
            <p className="text-[12px] text-[#71717A] mt-1">
              Single source of truth for the listing <b>form fields</b> AND the public <b>search filters</b>. Filters link to form fields so options never duplicate.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeTab !== "project" && activeTab !== "property_types" && dirty && <span className="text-[11px] text-amber-600 font-semibold">Unsaved changes</span>}
            {activeTab !== "project" && activeTab !== "property_types" && (
              <>
                <Button onClick={runHealthCheck} disabled={healthLoading} variant="outline" className="rounded-sm" data-testid="schema-health-check-btn">
                  <Activity size={13} className="mr-1.5" /> {healthLoading ? "Checking…" : "Health Check"}
                </Button>
                <Button onClick={save} disabled={!dirty || saving} className="btn-brand rounded-sm" data-testid="schema-save-btn">
                  <Save size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save Schema"}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-zinc-200 mb-4" data-testid="schema-tabs">
          {SCHEMA_TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 text-[13px] font-semibold inline-flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === key ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
              data-testid={`schema-tab-${key}`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Inline warnings */}
        {activeTab !== "project" && activeTab !== "property_types" && warnings.length > 0 && (
          <div className="space-y-1.5 mb-4" data-testid="schema-warnings">
            {warnings.map((w, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 text-[12px] px-3 py-2 rounded-sm border ${
                  w.severity === "error" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"
                }`}
                data-testid={`schema-warning-${w.kind}`}
              >
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>{w.msg}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tab body */}
        {activeTab === "fields" ? (
          <FormFieldsTab
            fields={schema.fields}
            onUpdate={updateField}
            onDelete={removeField}
            onEdit={(f) => setEditingField(f)}
            onAdd={() => setShowAddField(true)}
          />
        ) : activeTab === "filters" ? (
          <SearchFiltersTab
            filters={schema.filters}
            fields={schema.fields}
            onUpdate={updateFilter}
            onDelete={removeFilter}
            onEdit={(f) => setEditingFilter(f)}
            onAdd={() => setShowAddFilter(true)}
          />
        ) : activeTab === "property_types" ? (
          <AdminPropertyTypesTab />
        ) : (
          <AdminProjectSchemaTab />
        )}
      </div>

      {/* Field edit dialog */}
      {editingField && (
        <FieldEditDialog
          field={editingField}
          onClose={() => setEditingField(null)}
          onSave={(patch) => {
            updateField(editingField.key, patch);
            setEditingField(null);
          }}
        />
      )}

      {/* Filter edit dialog */}
      {editingFilter && (
        <FilterEditDialog
          filter={editingFilter}
          fields={schema.fields}
          onClose={() => setEditingFilter(null)}
          onSave={(patch) => {
            updateFilter(editingFilter.key, patch);
            setEditingFilter(null);
          }}
        />
      )}

      {/* Add field dialog */}
      {showAddField && (
        <FieldEditDialog
          field={DEFAULT_FIELD}
          isNew
          existingKeys={new Set(schema.fields.map((f) => f.key))}
          onClose={() => setShowAddField(false)}
          onSave={(patch) => {
            addField(patch);
            setShowAddField(false);
          }}
        />
      )}

      {/* Add filter dialog */}
      {showAddFilter && (
        <FilterEditDialog
          filter={DEFAULT_FILTER}
          fields={schema.fields}
          isNew
          existingKeys={new Set(schema.filters.map((f) => f.key))}
          onClose={() => setShowAddFilter(false)}
          onSave={(patch) => {
            addFilter(patch);
            setShowAddFilter(false);
          }}
        />
      )}

      {/* Health Check report dialog */}
      {healthReport && (
        <SchemaHealthDialog
          report={healthReport}
          onClose={() => setHealthReport(null)}
          onSchemaChanged={() => { load({ silent: true }); refreshListingSchema?.(); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Schema Health Check dialog — Auto-fixable / Manual / Informational
// ───────────────────────────────────────────────────────────────
const HEALTH_BLOCK_LABELS = {
  duplicate_field_keys: "Duplicate field keys",
  schema_fields_missing_storage_key: "Schema fields missing storage key",
  alias_overlap_with_canonical: "Aliases that overlap a canonical key",
  filters_missing_field: "Filters linked to missing fields",
  required_hidden_from_form: "Required fields hidden from form",
  empty_dropdown_options: "Dropdown fields with empty options",
  internal_fields_exposed_to_public: "Internal fields exposed to public",
  media_fields_missing_gallery_renderer: "Media fields missing gallery renderer",
  url_fields_missing_link_renderer: "URL fields missing link renderer",
  duplicate_zone_placement: "Duplicate display-zone placement",
  field_with_no_zone: "Fields with no display zone",
  backend_fields_missing_from_schema: "Backend fields missing from schema",
  filter_options_duplicated: "Filter options duplicated (not inherited)",
};

const FIX_GROUPS = [
  { cls: "auto", title: "Auto-fixable", sub: "Safe schema-metadata fixes — no listing data touched", border: "border-emerald-200", bg: "bg-emerald-50/40", text: "text-emerald-800" },
  { cls: "manual", title: "Manual edit required", sub: "Needs an admin decision — follow each hint", border: "border-red-200", bg: "bg-red-50/30", text: "text-red-800" },
  { cls: "info", title: "Informational", sub: "No action required", border: "border-zinc-200", bg: "bg-zinc-50/40", text: "text-zinc-700" },
];

function HealthIssueItem({ item }) {
  if (typeof item === "string") return <div className="font-mono text-[11px] break-all">{item}</div>;
  const name = item.key || item.field_key || item.filter_key || "";
  return (
    <div className="py-1">
      {name && <code className="text-[11px] font-mono bg-zinc-100 px-1.5 py-0.5 rounded-sm text-zinc-700">{name}</code>}
      <div className="text-[11.5px] text-zinc-700 mt-0.5 leading-relaxed">
        {item.hint || JSON.stringify(item)}
      </div>
    </div>
  );
}

function SchemaHealthDialog({ report: initialReport, onClose, onSchemaChanged }) {
  const [report, setReport] = useState(initialReport);
  const [fixing, setFixing] = useState(false);
  const [fixes, setFixes] = useState(null);

  const meta = report.issue_meta || {};
  const groups = { auto: [], manual: [], info: [] };
  for (const [cat, items] of Object.entries(report.issues || {})) {
    const cls = groups[meta[cat]] ? meta[cat] : "manual";
    if ((items || []).length > 0) groups[cls].push({ cat, items });
  }
  const autoCount = report.auto_fixable_count ?? groups.auto.reduce((n, g) => n + g.items.length, 0);

  const runAutoFix = async () => {
    setFixing(true);
    try {
      const r = await api.post("/admin/listing-schema/health-check/auto-fix");
      setFixes(r.data.fixes_applied || []);
      setReport(r.data.report);
      toast.success(`${r.data.fixes_count} safe issue${r.data.fixes_count === 1 ? "" : "s"} fixed — health check re-run`);
      onSchemaChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto fix failed");
    }
    setFixing(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="schema-health-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl inline-flex items-center gap-2">
            <Activity size={18} /> Schema Health Check
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${report.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="health-summary">
            {report.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            <span className="text-[12px] font-semibold">
              {report.ok ? "All critical checks passing." : `${report.critical_count} critical issue${report.critical_count === 1 ? "" : "s"} need attention.`}
              {" "}{report.warning_count} warning{report.warning_count === 1 ? "" : "s"}.
              {" · "}{report.field_count} fields · {report.filter_count} filters
            </span>
          </div>

          {/* Auto Fix bar */}
          <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm border border-zinc-200 bg-white flex-wrap" data-testid="health-classification-bar">
            <div className="text-[12px] text-zinc-700 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid="health-auto-count">{autoCount} auto-fixable</span>
              <span className="inline-flex items-center gap-1 text-red-700 font-semibold" data-testid="health-manual-count">{report.manual_count ?? groups.manual.reduce((n, g) => n + g.items.length, 0)} manual</span>
              <span className="inline-flex items-center gap-1 text-zinc-500 font-semibold" data-testid="health-info-count">{report.info_count ?? groups.info.reduce((n, g) => n + g.items.length, 0)} informational</span>
            </div>
            <Button
              size="sm"
              onClick={runAutoFix}
              disabled={fixing || autoCount === 0}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="auto-fix-safe-btn"
            >
              {fixing ? "Fixing…" : `Auto Fix Safe Issues${autoCount > 0 ? ` (${autoCount})` : ""}`}
            </Button>
          </div>

          {/* Applied fixes panel */}
          {fixes && (
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm" data-testid="autofix-results">
              <div className="px-3 py-2 border-b border-emerald-200 text-[12px] font-semibold text-emerald-800 inline-flex items-center gap-2">
                <CheckCircle2 size={12} /> {fixes.length === 0 ? "No safe fixes were needed" : `${fixes.length} fix${fixes.length === 1 ? "" : "es"} applied`}
              </div>
              {fixes.length > 0 && (
                <div className="px-3 py-2 space-y-1">
                  {fixes.map((fx, i) => (
                    <div key={i} className="text-[11.5px] text-zinc-700 leading-relaxed">• {fx.detail}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Issue groups */}
          {FIX_GROUPS.map((g) => {
            const blocks = groups[g.cls];
            return (
              <div key={g.cls} className={`border rounded-sm ${g.border} ${g.bg}`} data-testid={`health-group-${g.cls === "auto" ? "auto-fixable" : g.cls}`}>
                <div className={`px-3 py-2 border-b text-[12px] font-semibold inline-flex items-center gap-2 w-full ${g.text} ${g.border}`}>
                  {blocks.length === 0 ? <CheckCircle2 size={12} className="text-emerald-600" /> : <AlertTriangle size={12} />}
                  {g.title}
                  <span className="text-[10px] uppercase tracking-wider font-normal opacity-70">{g.sub}</span>
                </div>
                {blocks.length === 0 ? (
                  <div className="px-3 py-2 text-[11px] text-zinc-500">No issues in this group.</div>
                ) : (
                  <div className="divide-y divide-zinc-200/60">
                    {blocks.map((b) => (
                      <div key={b.cat} className="px-3 py-2" data-testid={`health-block-${b.cat}`}>
                        <div className="text-[11.5px] font-semibold text-zinc-800">
                          {HEALTH_BLOCK_LABELS[b.cat] || b.cat}
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-500">{b.items.length} issue{b.items.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="mt-1 space-y-1">
                          {b.items.map((item, i) => <HealthIssueItem key={i} item={item} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-sm" data-testid="health-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────
// Form Fields tab
// ───────────────────────────────────────────────────────────────
function FormFieldsTab({ fields, onUpdate, onDelete, onEdit, onAdd }) {
  // Group by section
  const grouped = useMemo(() => {
    const map = {};
    for (const sec of FIELD_SECTIONS) map[sec] = [];
    map._Other = [];
    for (const f of fields) {
      const sec = map[f.section] !== undefined ? f.section : "_Other";
      map[sec].push(f);
    }
    // Sort each section
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [fields]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={onAdd} className="btn-brand rounded-sm" size="sm" data-testid="add-form-field-btn">
          <Plus size={12} className="mr-1.5" /> Add Field
        </Button>
      </div>

      {Object.entries(grouped).map(([section, list]) => (
        list.length === 0 ? null : (
          <SectionBlock key={section} title={section === "_Other" ? "Other" : section} count={list.length}>
            <div className="space-y-2">
              {list.map((f) => (
                <FieldRow key={f.key} field={f} onUpdate={onUpdate} onEdit={onEdit} onDelete={onDelete} />
              ))}
            </div>
          </SectionBlock>
        )
      ))}
    </div>
  );
}

function SectionBlock({ title, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-zinc-200 rounded-sm bg-zinc-50/30">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold text-zinc-700 hover:bg-zinc-100"
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span className="inline-flex items-center gap-1.5">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {title}
        </span>
        <span className="text-[11px] text-zinc-500">{count} field{count === 1 ? "" : "s"}</span>
      </button>
      {open && <div className="p-3 border-t border-zinc-200 bg-white">{children}</div>}
    </div>
  );
}

function FieldRow({ field, onUpdate, onEdit, onDelete }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid={`field-row-${field.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[13px] text-zinc-900">{field.label}</span>
            <code className="text-[11px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm font-mono">{field.key}</code>
            <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-100">{field.type}</span>
            {field.core ? (
              <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-700 border border-purple-100" title="Backend Property model column">Core</span>
            ) : (
              <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-zinc-50 text-zinc-600 border border-zinc-200" title="custom_fields.* dynamic field">Custom</span>
            )}
            {field.locked && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-zinc-200 text-zinc-700"><Lock size={9} /> Locked</span>}
            {field.required && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-100">Required</span>}
            {!field.showOnForm && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">Hidden on form</span>}
            {field.usedInFilters && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-100" title="A search filter is linked to this field">Linked Filter</span>}
            {field.displayVisibility?.apiInternalOnly && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-rose-100 text-rose-800 border border-rose-200" title="API internal only — never rendered on any UI surface">Internal Only</span>}
            {!field.displayVisibility?.apiInternalOnly && !field.displayVisibility?.publicDetail && (field.displayVisibility?.adminDetail || field.displayVisibility?.crm) && (
              <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-100" title="Hidden from public surfaces; visible to admin/team/CRM only">Admin Only</span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 space-y-0.5">
            <div>
              Section: {field.section} · Sort: {field.sortOrder} · Options: {field.options?.length || 0}
              {field.storageKey && (
                <> · Storage: <code className="text-zinc-700">{field.storageKey}</code></>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {field.usedInForm && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-100">in Listing Form</span>}
              {field.usedInEdit && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-100">in Edit Form</span>}
              {field.usedInFilters && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-100">in Filters</span>}
              {field.propertyTypes?.length > 0 && <span className="text-zinc-600">Types: {field.propertyTypes.join(", ")}</span>}
              {field.transactionTypes?.length > 0 && <span className="text-zinc-600">Txn: {field.transactionTypes.join(", ")}</span>}
            </div>
            {field.aliases?.length > 0 && (
              <div className="text-[10px] text-amber-700">
                <span className="font-semibold uppercase tracking-wider">Aliases:</span>{" "}
                {field.aliases.map((a) => <code key={a} className="bg-amber-50 px-1 rounded-sm border border-amber-100 mr-1">{a}</code>)}
              </div>
            )}
            <div className="text-[10px] text-zinc-500 flex items-center gap-1 flex-wrap">
              <span className="font-semibold uppercase tracking-wider">Renders:</span>
              <code className="bg-zinc-100 px-1 rounded-sm border border-zinc-200">{field.displayRenderer || "text"}</code>
              {DISPLAY_SURFACES.filter((s) => s !== "apiInternalOnly" && field.displayVisibility?.[s]).map((s) => (
                <span key={s} className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-sm border border-blue-100 font-semibold">{s}</span>
              ))}
              {field.displayVisibility?.apiInternalOnly && (
                <span className="bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded-sm border border-rose-200 font-semibold">internal-only</span>
              )}
            </div>
            {/* iter_111 — display zones chips */}
            {(() => {
              const zones = Object.entries(field.displayZones || {}).filter(([k, v]) => v && k !== "internalOnly");
              if (zones.length === 0 && !field.displayZones?.internalOnly) return null;
              return (
                <div className="text-[10px] text-zinc-500 flex items-center gap-1 flex-wrap">
                  <span className="font-semibold uppercase tracking-wider">Zones:</span>
                  {zones.map(([z]) => (
                    <span key={z} className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-sm border border-indigo-100 font-semibold">{z}</span>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => onUpdate(field.key, { active: !field.active })}
            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1 ${field.active ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-zinc-100 text-zinc-500"}`}
            data-testid={`field-active-toggle-${field.key}`}
          >
            {field.active ? <Eye size={10} /> : <EyeOff size={10} />}
            {field.active ? "Active" : "Inactive"}
          </button>
          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => onEdit(field)} data-testid={`field-edit-${field.key}`}>Edit</Button>
          {!field.locked && (
            <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-red-600" onClick={() => onDelete(field.key)} data-testid={`field-delete-${field.key}`}>
              <Trash2 size={12} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Search Filters tab
// ───────────────────────────────────────────────────────────────
function SearchFiltersTab({ filters, fields, onUpdate, onDelete, onEdit, onAdd }) {
  const sorted = useMemo(() => [...filters].sort((a, b) => a.sortOrder - b.sortOrder), [filters]);
  const fieldByKey = Object.fromEntries(fields.map((f) => [f.key, f]));

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={onAdd} className="btn-brand rounded-sm" size="sm" data-testid="add-search-filter-btn">
          <Plus size={12} className="mr-1.5" /> Add Filter
        </Button>
      </div>

      {sorted.map((fl) => {
        const linked = fieldByKey[fl.linkedFieldKey];
        return (
          <div key={fl.key} className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid={`filter-row-${fl.key}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[13px] text-zinc-900">{fl.label}</span>
                  <code className="text-[11px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm font-mono">{fl.key}</code>
                  <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-100">{fl.uiType}</span>
                  <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-100">{fl.placement}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Linked to field: <code className="text-zinc-700">{fl.linkedFieldKey}</code>
                  {linked ? <span className="ml-1 text-zinc-400">({linked.options.length} options inherited)</span> : <span className="ml-1 text-red-600 font-semibold">⚠ missing field</span>}
                  {" · "}Sort: {fl.sortOrder}
                  {!fl.mobileVisible && " · Mobile: hidden"}
                  {!fl.desktopVisible && " · Desktop: hidden"}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => onUpdate(fl.key, { active: !fl.active })}
                  className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1 ${fl.active ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-zinc-100 text-zinc-500"}`}
                  data-testid={`filter-active-toggle-${fl.key}`}
                >
                  {fl.active ? <Eye size={10} /> : <EyeOff size={10} />}
                  {fl.active ? "Active" : "Inactive"}
                </button>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => onEdit(fl)} data-testid={`filter-edit-${fl.key}`}>Edit</Button>
                <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-red-600" onClick={() => onDelete(fl.key)} data-testid={`filter-delete-${fl.key}`}>
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Field edit dialog
// ───────────────────────────────────────────────────────────────
function FieldEditDialog({ field, onClose, onSave, isNew = false, existingKeys = new Set() }) {
  const [draft, setDraft] = useState(field);
  const [optionText, setOptionText] = useState((field.options || []).join("\n"));

  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (isNew) {
      if (!/^[a-z][a-z0-9_]{1,39}$/.test(draft.key)) { toast.error("Key must start with a letter, lowercase/underscore/digits only"); return; }
      if (existingKeys.has(draft.key)) { toast.error("Key already exists"); return; }
    }
    if (!draft.label.trim()) { toast.error("Label is required"); return; }
    if (!FIELD_TYPES.includes(draft.type)) { toast.error("Invalid field type"); return; }

    const opts = optionText.split("\n").map((s) => s.trim()).filter(Boolean);
    onSave({ ...draft, options: opts });
  };

  const needsOptions = ["dropdown", "multi-select", "floor-dropdown"].includes(draft.type);
  const useFloorPreset = () => setOptionText(DEFAULT_FLOOR_OPTIONS.join("\n"));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="field-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isNew ? "New Form Field" : `Edit Field — ${draft.label}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <LabelInput label="Backend Key (immutable)" value={draft.key} onChange={(v) => patch("key", v)} disabled={!isNew} testid="field-edit-key" />
            <LabelInput label="Display Label" value={draft.label} onChange={(v) => patch("label", v)} testid="field-edit-label" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <LabelSelect label="Type" value={draft.type} onChange={(v) => patch("type", v)} options={FIELD_TYPES} testid="field-edit-type" />
            <LabelSelect label="Section" value={draft.section} onChange={(v) => patch("section", v)} options={FIELD_SECTIONS} testid="field-edit-section" />
            <LabelInput label="Sort Order" type="number" value={draft.sortOrder} onChange={(v) => patch("sortOrder", Number(v) || 0)} testid="field-edit-sort" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LabelInput label="Placeholder" value={draft.placeholder} onChange={(v) => patch("placeholder", v)} testid="field-edit-placeholder" />
            <LabelInput label="Help Text" value={draft.helpText} onChange={(v) => patch("helpText", v)} testid="field-edit-helptext" />
          </div>
          {needsOptions && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Options (one per line)</div>
                {draft.type === "floor-dropdown" && (
                  <Button size="sm" variant="outline" type="button" className="rounded-sm h-7 text-[11px]" onClick={useFloorPreset}>Use floor preset</Button>
                )}
              </div>
              <Textarea
                rows={6}
                value={optionText}
                onChange={(e) => setOptionText(e.target.value)}
                className="rounded-sm font-mono text-[12px]"
                placeholder="Option A&#10;Option B"
                data-testid="field-edit-options"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <ToggleRow label="Show on Submission Form" value={draft.showOnForm} onChange={(v) => patch("showOnForm", v)} testid="field-edit-showOnForm" />
            <ToggleRow label="Required" value={draft.required} onChange={(v) => patch("required", v)} testid="field-edit-required" />
            <ToggleRow label="Active" value={draft.active} onChange={(v) => patch("active", v)} testid="field-edit-active" />
          </div>
          <CSVField label="Applicable Property Types (blank = all)" value={(draft.propertyTypes || []).join(", ")} onChange={(arr) => patch("propertyTypes", arr)} testid="field-edit-propertyTypes" />
          <CSVField label="Applicable Transaction Types (blank = all)" value={(draft.transactionTypes || []).join(", ")} onChange={(arr) => patch("transactionTypes", arr)} options={TRANSACTION_TYPES} testid="field-edit-transactionTypes" />
          <CSVField label="Aliases (legacy keys for old DB records)" value={(draft.aliases || []).join(", ")} onChange={(arr) => patch("aliases", arr)} testid="field-edit-aliases" />
          <RoleVisibilityEditor label="Role Visibility" value={draft.roleVisibility} onChange={(v) => patch("roleVisibility", v)} testid="field-edit-roleVisibility" />
          {/* iter_110 — Display Visibility & Renderer */}
          <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/40">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-2">Display Visibility (where the field MAY render)</div>
            <div className="grid grid-cols-2 gap-1.5">
              {DISPLAY_SURFACES.map((s) => {
                const on = draft.displayVisibility?.[s] !== false;
                const isInternalOnly = s === "apiInternalOnly";
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => {
                      const next = { ...(draft.displayVisibility || {}) };
                      next[s] = !next[s];
                      // apiInternalOnly is the master kill switch — flips all others off when ON
                      if (s === "apiInternalOnly" && next[s]) {
                        for (const k of DISPLAY_SURFACES) {
                          if (k !== "apiInternalOnly") next[k] = false;
                        }
                      }
                      patch("displayVisibility", next);
                    }}
                    className={`flex items-center justify-between text-[11px] font-semibold px-2.5 py-1.5 rounded-sm border transition-colors ${
                      on
                        ? (isInternalOnly ? "bg-rose-100 text-rose-800 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200")
                        : "bg-zinc-100 text-zinc-500 border-zinc-200"
                    }`}
                    data-testid={`field-edit-displayVisibility-${s}`}
                  >
                    <span>{DISPLAY_SURFACE_LABELS[s]}</span>
                    <span className="text-[10px] uppercase tracking-wider">{on ? "ON" : "OFF"}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">
              <b>API Internal Only</b> is the kill switch — when ON, the field is never rendered anywhere. Use for IDs, coords, raw paths.
            </div>
          </div>
          <LabelSelect label="Display Renderer" value={draft.displayRenderer || "text"} onChange={(v) => patch("displayRenderer", v)} options={RENDERER_HINTS} testid="field-edit-displayRenderer" />
          {/* iter_111 — Display Zones (WHERE in the detail-page layout) */}
          <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/40">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-2">Display Zones (where in the public detail page)</div>
            <div className="grid grid-cols-2 gap-1.5">
              {DISPLAY_ZONES.map((z) => {
                const on = draft.displayZones?.[z] === true;
                const isInternal = z === "internalOnly";
                return (
                  <button
                    type="button"
                    key={z}
                    onClick={() => {
                      const next = { ...(draft.displayZones || {}) };
                      next[z] = !next[z];
                      // internalOnly is a kill switch — flips all others OFF
                      if (z === "internalOnly" && next[z]) {
                        for (const k of DISPLAY_ZONES) if (k !== "internalOnly") next[k] = false;
                      }
                      patch("displayZones", next);
                    }}
                    className={`flex items-center justify-between text-[11px] font-semibold px-2.5 py-1.5 rounded-sm border transition-colors ${
                      on
                        ? (isInternal ? "bg-rose-100 text-rose-800 border-rose-200" : "bg-indigo-50 text-indigo-700 border-indigo-200")
                        : "bg-zinc-100 text-zinc-500 border-zinc-200"
                    }`}
                    data-testid={`field-edit-displayZone-${z}`}
                  >
                    <span>{DISPLAY_ZONE_LABELS[z]}</span>
                    <span className="text-[10px] uppercase tracking-wider">{on ? "ON" : "OFF"}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">
              A field in <b>Primary Specs</b> automatically <b>excludes</b> from <b>More Details</b> on the detail page (dedupe by canonical key + aliases).
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} className="btn-brand rounded-sm" data-testid="field-edit-save">{isNew ? "Add Field" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────
// Filter edit dialog
// ───────────────────────────────────────────────────────────────
function FilterEditDialog({ filter, fields, onClose, onSave, isNew = false, existingKeys = new Set() }) {
  const [draft, setDraft] = useState(filter);

  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const linked = fields.find((f) => f.key === draft.linkedFieldKey);

  const handleSave = () => {
    if (!draft.linkedFieldKey) { toast.error("Pick a linked field first"); return; }
    if (isNew) {
      const k = draft.key || `f_${draft.linkedFieldKey}`;
      if (existingKeys.has(k)) { toast.error("Filter key already exists"); return; }
      draft.key = k;
    }
    if (!FILTER_UI_TYPES.includes(draft.uiType)) { toast.error("Invalid UI type"); return; }
    if (!FILTER_PLACEMENTS.includes(draft.placement)) { toast.error("Invalid placement"); return; }
    onSave({ ...draft, label: draft.label || linked?.label || draft.linkedFieldKey });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="filter-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isNew ? "New Search Filter" : `Edit Filter — ${draft.label}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-1">Linked Form Field</div>
            <Select value={draft.linkedFieldKey} onValueChange={(v) => patch("linkedFieldKey", v)}>
              <SelectTrigger className="rounded-sm" data-testid="filter-edit-linkedFieldKey"><SelectValue placeholder="Pick a field…" /></SelectTrigger>
              <SelectContent>
                {fields.map((f) => (
                  <SelectItem key={f.key} value={f.key}>{f.label} ({f.key})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {linked && (
              <div className="text-[11px] text-zinc-500 mt-1">
                Field type: <code>{linked.type}</code>{" · "}
                Inherited options: {linked.options.length}
                {!linked.active && <span className="text-amber-700 font-semibold ml-2">⚠ linked field is INACTIVE</span>}
              </div>
            )}
          </div>
          <LabelInput label="Filter Label (override)" value={draft.label} onChange={(v) => patch("label", v)} testid="filter-edit-label" />
          <div className="grid grid-cols-3 gap-3">
            <LabelSelect label="UI Type" value={draft.uiType} onChange={(v) => patch("uiType", v)} options={FILTER_UI_TYPES} testid="filter-edit-uiType" />
            <LabelSelect label="Placement" value={draft.placement} onChange={(v) => patch("placement", v)} options={FILTER_PLACEMENTS} testid="filter-edit-placement" />
            <LabelInput label="Sort Order" type="number" value={draft.sortOrder} onChange={(v) => patch("sortOrder", Number(v) || 0)} testid="filter-edit-sort" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ToggleRow label="Active" value={draft.active} onChange={(v) => patch("active", v)} testid="filter-edit-active" />
            <ToggleRow label="Default Collapsed" value={draft.defaultCollapsed} onChange={(v) => patch("defaultCollapsed", v)} testid="filter-edit-defaultCollapsed" />
            <ToggleRow label="Visible on Mobile" value={draft.mobileVisible} onChange={(v) => patch("mobileVisible", v)} testid="filter-edit-mobileVisible" />
            <ToggleRow label="Visible on Desktop" value={draft.desktopVisible} onChange={(v) => patch("desktopVisible", v)} testid="filter-edit-desktopVisible" />
          </div>
          <RoleVisibilityEditor label="Filter Visibility" value={draft.visibility} onChange={(v) => patch("visibility", v)} testid="filter-edit-visibility" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} className="btn-brand rounded-sm" data-testid="filter-edit-save">{isNew ? "Add Filter" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────
// Small primitives
// ───────────────────────────────────────────────────────────────
function LabelInput({ label, value, onChange, type = "text", disabled = false, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-1">{label}</div>
      <Input type={type} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="rounded-sm" data-testid={testid} />
    </label>
  );
}

function LabelSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-1">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {[...options].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}

function ToggleRow({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 text-[12px] font-semibold text-zinc-800 cursor-pointer border border-zinc-200 rounded-sm px-3 py-2 bg-zinc-50/40">
      {label}
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

function CSVField({ label, value, onChange, options = null, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-1">{label}</div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        placeholder={options ? options.join(", ") : "Comma-separated, e.g. Builder Floor, Apartment"}
        className="rounded-sm text-[12px]"
        data-testid={testid}
      />
    </div>
  );
}

function RoleVisibilityEditor({ label, value, onChange, testid }) {
  const safe = value || {};
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {ROLES.map((r) => {
          const on = safe[r] !== false;
          return (
            <button
              type="button"
              key={r}
              onClick={() => onChange({ ...safe, [r]: !on })}
              className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border ${
                on ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"
              }`}
              data-testid={`${testid}-${r}`}
            >
              {r}
            </button>
          );
        })}
      </div>
    </div>
  );
}
